import React, { useState, useEffect } from 'react';
import LegacyTransactionUploaderLazy from './LegacyTransactionUploaderLazy';
import { getBuildings } from 'services/api/buildings';
import { Building } from 'services/api/buildings';
import SvgSpinner from 'components/Spinners/SvgSpinner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Combobox } from 'components/ui/combobox';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from 'components/ui/alert-dialog';
import { AlertCircle } from 'lucide-react';

const LegacyDefaultPage: React.FC = () => {
    const [buildings, setBuildings] = useState<Building[]>([]);
    const [selectedBuilding, setSelectedBuilding] = useState<string | null>(null);
    const [selectedBuildingInfo, setSelectedBuildingInfo] = useState<Building | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showDisabledAlert, setShowDisabledAlert] = useState<boolean>(false);
    const [disabledBuildingName, setDisabledBuildingName] = useState<string>('');

    useEffect(() => {
        const fetchBuildings = async () => {
            try {
                // API đã xử lý lọc 'old' và loại bỏ số 0 đầu; giữ nguyên thứ tự trả về
                const buildingsData = await getBuildings();
                setBuildings(buildingsData);
            } catch (err) {
                if (err instanceof Error) {
                    setError(err.message);
                } else {
                    setError('An unknown error occurred');
                }
                console.error("Failed to load buildings:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchBuildings();
    }, []);

    useEffect(() => {
        if (selectedBuilding && buildings.length > 0) {
            const buildingInfo = buildings.find(b => b.id === selectedBuilding);
            setSelectedBuildingInfo(buildingInfo || null);
        } else {
            setSelectedBuildingInfo(null);
        }
    }, [selectedBuilding, buildings]);

    const handleBuildingSelection = (buildingId: string) => {
        const building = buildings.find(b => b.id === buildingId);
        if (building) {
            // Check if building is disabled
            if (building.is_enabled === false) {
                setDisabledBuildingName(`${building.name} - ${building.descri ?? ''}`.trim());
                setShowDisabledAlert(true);
                return; // Don't set selectedBuilding
            }
        }
        setSelectedBuilding(buildingId);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full py-10">
                <SvgSpinner />
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="space-y-6 pt-2">
            <Card>
                <CardHeader>
                    <CardTitle>Analisi Estratto Conto Bancario</CardTitle>
                    <CardDescription>
                        Seleziona uno stabile per poter caricare l'estratto c/c bancario e lanciare l'analisi.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Combobox
                        options={buildings.map(building => ({
                            value: building.id,
                            label: `${building.name} - ${building.descri ?? ''}`.trim()
                        }))}
                        value={selectedBuilding || ''}
                        onChange={handleBuildingSelection}
                        placeholder="Seleziona uno stabile..."
                        searchPlaceholder="Cerca per nome..."
                        className="w-[300px]"
                    />
                </CardContent>
            </Card>

            {selectedBuilding && selectedBuildingInfo && (
                <Card>
                    <CardContent className="pt-6">
                        <LegacyTransactionUploaderLazy 
                            buildingId={selectedBuilding} 
                            organizationId={selectedBuildingInfo.organizationId}
                        />
                    </CardContent>
                </Card>
            )}

            {/* Disabled Building Alert Dialog */}
            <AlertDialog open={showDisabledAlert} onOpenChange={setShowDisabledAlert}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-red-500" />
                            Stabile Disabilitato
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Il stabile <strong>"{disabledBuildingName}"</strong> è attualmente disabilitato e non è possibile effettuare operazioni su di esso.
                            <br /><br />
                            Contatta l'amministratore per maggiori informazioni o per riabilitare questo stabile.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogAction onClick={() => setShowDisabledAlert(false)}>
                            Ho Capito
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default LegacyDefaultPage;
